import { useState } from 'react';
import { useSWRConfig } from 'swr';
import { useApiServices } from '@shared/lib/use-api-services';
import { swrKeys } from '@shared/api/swr/keys';
import {
  mapProjectFormToCreatePayload,
  mapProjectFormToUpdatePayload,
  type ProjectFormValues,
} from '@entities/project';
import type { ProjectDto } from '@shared/api/contracts';

export const useSaveProject = (originalProject?: ProjectDto) => {
  const services = useApiServices();
  const { mutate } = useSWRConfig();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const saveProject = async (values: ProjectFormValues) => {
    setIsSaving(true);
    setError(null);

    try {
      if (values.id) {
        await services.projects.editProject(
          values.id,
          mapProjectFormToUpdatePayload(values, originalProject),
        );
        if (values.ips.length > 0) {
          await services.projects.updateProjectIpWhitelist(values.id, values.ips);
        }
      } else {
        await services.projects.createProject(mapProjectFormToCreatePayload(values));
      }

      await mutate(swrKeys.projects.all());
      return { success: true as const };
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || 'Failed to save project';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setIsSaving(false);
    }
  };

  const deleteProject = async (id: string) => {
    setIsDeleting(true);
    setError(null);

    try {
      await services.projects.deleteProject(id);
      await mutate(swrKeys.projects.all(), undefined, { revalidate: true });
      return { success: true as const };
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message || 'Failed to delete project';
      setError(message);
      return { success: false as const, error: message };
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    saveProject,
    deleteProject,
    isSaving,
    isDeleting,
    error,
  };
};
